// src/utils/artistDashboardApi.js
// Direct SQL implementation of get_all_artists_with_search_safe RPC function

import { supabase } from './supabaseClient';

/**
 * Fetch artists with enhanced data - Direct SQL version of your RPC function
 */
export const fetchArtistsEnhanced = async (
  searchQuery = '', 
  limit = 20, 
  offset = 0, 
  sortColumn = 'created_at', 
  sortDirection = 'desc',
  memberFilter = 'all'
) => {
  try {
    console.log('Fetching artists with params:', { searchQuery, limit, offset, sortColumn, sortDirection, memberFilter });

    // Build the main query for artists
    let artistQuery = supabase
      .from('users')
      .select(`
        uuid,
        first_name,
        last_name,
        nickname,
        email,
        handle,
        profile_img,
        customer_id,
        created_at,
        updated_at,
        mobile_phone,
        instagram,
        agreed_to_terms,
        agreed_to_terms_at,
        preferences,
        commissions,
        bio,
        location,
        cover_image,
        test_account
      `, { count: 'exact' })
      .eq('test_account', false);

    // Add search filters (fuzzy, many fields)
    if (searchQuery && searchQuery !== '') {
      const like = `%${searchQuery}%`;
      const orClause = [
        `first_name.ilike.${like}`,
        `last_name.ilike.${like}`,
        `nickname.ilike.${like}`,
        `email.ilike.${like}`,
        `handle.ilike.${like}`
      ].join(',');
      artistQuery = artistQuery.or(orClause);
    }

    // Apply sorting
    artistQuery = artistQuery.order(sortColumn, { ascending: sortDirection === 'asc' });

    // Apply pagination
    artistQuery = artistQuery.range(offset, offset + limit - 1);

    const { data: artists, error: artistError, count } = await artistQuery;
    if (artistError) throw artistError;

    // Get artwork statistics for each artist
    const artistsWithStats = await Promise.all(
      artists.map(async (artist) => {
        // Get artwork counts and statistics
        const { data: artworkStats, error: artworkError } = await supabase
          .from('vcs')
          .select('vc_id, created_at, price, status, access_control')
          .eq('uuid', artist.uuid)
          .eq('access_control', 'Public');

        if (artworkError) {
          console.warn(`Error fetching artwork for ${artist.email}:`, artworkError);
        }

        const artworks = artworkStats || [];
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Calculate artwork statistics
        const artworkCount = artworks.length;
        const publicArtworkCount = artworks.filter(a => a.status === 'Public').length;
        const pricedArtworkCount = artworks.filter(a => a.price && a.price > 0).length;
        const artworksLast30Days = artworks.filter(a => new Date(a.created_at) >= thirtyDaysAgo).length;
        const lastArtworkDate = artworks.length > 0 
          ? new Date(Math.max(...artworks.map(a => new Date(a.created_at))))
          : null;

        // Determine artist activity level
        let artistActivity = 'no_artworks';
        if (artworkCount > 0) {
          if (artworksLast30Days > 0) {
            artistActivity = 'very_active';
          } else if (lastArtworkDate && lastArtworkDate >= new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)) {
            artistActivity = 'active';
          } else if (lastArtworkDate && lastArtworkDate >= new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)) {
            artistActivity = 'moderate';
          } else {
            artistActivity = 'inactive';
          }
        }

        // Get user roles
        const { data: userRoles, error: roleError } = await supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_id', artist.uuid);

        if (roleError) {
          console.warn(`Error fetching roles for ${artist.email}:`, roleError);
        }

        const roles = userRoles || [];
        let primaryRoleId = null;
        let member = null;

        // Determine primary role (prioritize member role)
        if (roles.some(r => r.role_id === 1)) {
          primaryRoleId = 1;
          member = true;
        } else if (roles.some(r => r.role_id === 4)) {
          primaryRoleId = 4;
          member = false;
        } else if (roles.some(r => r.role_id === 3)) {
          primaryRoleId = 3;
          member = null;
        }

        // Apply member filter
        if (memberFilter === 'members' && !member) return null;
        if (memberFilter === 'non-members' && member !== false) return null;

        return {
          // Original fields matching your RPC function
          firstName: artist.first_name,
          lastName: artist.last_name,
          nickname: artist.nickname,
          email: artist.email,
          handle: artist.handle,
          customer_id: artist.customer_id,
          created_at: artist.created_at,
          profileImg: artist.profile_img,
          artworkCount,
          member,

          // New fields
          mobilePhone: artist.mobile_phone,
          instagram: artist.instagram,
          agreedToTerms: artist.agreed_to_terms,
          agreedToTermsAt: artist.agreed_to_terms_at,
          preferences: artist.preferences,
          selfIdentifiedAs: artist.preferences?.home,
          hasSelectedRole: artist.preferences?.home_select === true,
          commissions: artist.commissions,
          bio: artist.bio,
          location: artist.location,
          coverImage: artist.cover_image,
          isActiveArtist: artworkCount > 0,
          accountAge: Math.floor((now - new Date(artist.created_at)) / (1000 * 60 * 60 * 24)),

          // Enhanced artwork data
          artworkDetails: {
            totalPublicArtworks: artworkCount,
            publicStatusArtworks: publicArtworkCount,
            pricedArtworks: pricedArtworkCount,
            lastArtworkDate: lastArtworkDate?.toISOString(),
            artworksLast30Days,
            daysSinceLastArtwork: lastArtworkDate 
              ? Math.floor((now - lastArtworkDate) / (1000 * 60 * 60 * 24))
              : null,
            hasRecentArtwork: artworksLast30Days > 0,
            artistActivity
          },

          // Internal fields for processing
          primary_role_id: primaryRoleId,
          uuid: artist.uuid
        };
      })
    );

    // Filter out null results from member filtering
    const validArtists = artistsWithStats.filter(artist => artist !== null);

    // Calculate totals
    const totals = calculateArtistTotals(validArtists);

    // Get total artwork count for all artists (not just current page)
    const { count: totalArtworks } = await supabase
      .from('vcs')
      .select('*', { count: 'exact', head: true })
      .eq('access_control', 'Public');

    // Get global stats for top panel
    const { count: globalTotalArtists } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('test_account', false);

    const { count: globalMemberCount } = await supabase
      .from('user_roles')
      .select('*', { count: 'exact', head: true })
      .eq('role_id', 1);

    const { count: globalNonMemberCount } = await supabase
      .from('user_roles')
      .select('*', { count: 'exact', head: true })
      .eq('role_id', 4);

    const { count: globalSelfIdentifiedArtists } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('test_account', false)
      .eq('preferences->>home', 'artist');

    const { count: globalSelfIdentifiedCollectors } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('test_account', false)
      .eq('preferences->>home', 'collector');

    const { count: globalHasSelectedRole } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('test_account', false)
      .eq('preferences->>home_select', 'true');

    const { count: globalAgreedToTerms } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('test_account', false)
      .eq('agreed_to_terms', true);

    // Fix: Count unique artist uuids with at least one artwork
    const { data: allArtworks } = await supabase
      .from('vcs')
      .select('uuid')
      .eq('access_control', 'Public');
    const uniqueArtistUuids = new Set((allArtworks || []).map(a => a.uuid));
    const globalHasArtworks = uniqueArtistUuids.size;

    const { count: globalActiveArtists } = await supabase
      .from('vcs')
      .select('uuid', { count: 'exact', head: true })
      .eq('access_control', 'Public')
      .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

    const { count: globalTotalArtworks } = await supabase
      .from('vcs')
      .select('*', { count: 'exact', head: true })
      .eq('access_control', 'Public');

    const globalTotals = {
      totalArtists: globalTotalArtists || 0,
      memberCount: globalMemberCount || 0,
      nonMemberCount: globalNonMemberCount || 0,
      selfIdentifiedArtists: globalSelfIdentifiedArtists || 0,
      selfIdentifiedCollectors: globalSelfIdentifiedCollectors || 0,
      hasSelectedRole: globalHasSelectedRole || 0,
      agreedToTerms: globalAgreedToTerms || 0,
      hasArtworks: globalHasArtworks,
      activeArtists: globalActiveArtists || 0,
      totalArtworks: globalTotalArtworks || 0
    };

    return {
      artists: validArtists,
      totals: { ...totals, totalArtworks: totalArtworks || 0 },
      globalTotals,
      pagination: {
        limit,
        offset,
        total: count || 0,
        hasMore: (offset + limit) < (count || 0)
      }
    };

  } catch (error) {
    console.error('Error fetching artists enhanced:', error);
    return {
      artists: [],
      totals: {},
      globalTotals: {},
      pagination: { limit, offset, total: 0, hasMore: false }
    };
  }
};

/**
 * Calculate totals and statistics from artist data
 */
const calculateArtistTotals = (artists) => {
  const totals = {
    totalArtists: artists.length,
    memberCount: artists.filter(a => a.member === true).length,
    nonMemberCount: artists.filter(a => a.member === false).length,
    selfIdentifiedArtists: artists.filter(a => a.selfIdentifiedAs === 'artist').length,
    selfIdentifiedCollectors: artists.filter(a => a.selfIdentifiedAs === 'collector').length,
    hasSelectedRole: artists.filter(a => a.hasSelectedRole).length,
    agreedToTerms: artists.filter(a => a.agreedToTerms).length,
    hasArtworks: artists.filter(a => a.artworkCount > 0).length,
    activeArtists: artists.filter(a => a.artworkDetails.artworksLast30Days > 0).length,
    hasMobilePhone: artists.filter(a => a.mobilePhone).length,
    hasInstagram: artists.filter(a => a.instagram && a.instagram !== '').length,
    hasBio: artists.filter(a => a.bio && a.bio !== '').length,
    hasCommissions: artists.filter(a => a.commissions).length
  };

  // Artwork statistics
  const artistsWithArtwork = artists.filter(a => a.artworkCount > 0);
  totals.artworkStats = {
    totalArtworks: artists.reduce((sum, a) => sum + a.artworkCount, 0),
    totalPricedArtworks: artists.reduce((sum, a) => sum + a.artworkDetails.pricedArtworks, 0),
    artworksLast30Days: artists.reduce((sum, a) => sum + a.artworkDetails.artworksLast30Days, 0),
    averageArtworksPerArtist: artistsWithArtwork.length > 0 
      ? Math.round((artistsWithArtwork.reduce((sum, a) => sum + a.artworkCount, 0) / artistsWithArtwork.length) * 10) / 10
      : 0
  };

  // Activity breakdown
  totals.activityBreakdown = {
    veryActive: artists.filter(a => a.artworkDetails.artistActivity === 'very_active').length,
    active: artists.filter(a => a.artworkDetails.artistActivity === 'active').length,
    moderate: artists.filter(a => a.artworkDetails.artistActivity === 'moderate').length,
    inactive: artists.filter(a => a.artworkDetails.artistActivity === 'inactive').length,
    noArtworks: artists.filter(a => a.artworkDetails.artistActivity === 'no_artworks').length
  };

  return totals;
};

/**
 * Export artwork data for selected artists
 */
export const exportArtistData = async (artistUuids) => {
  try {
    const { data: artists } = await supabase
      .from('users')
      .select(`
        uuid,
        first_name,
        last_name,
        nickname,
        email,
        handle,
        created_at,
        preferences,
        mobile_phone,
        instagram
      `)
      .in('uuid', artistUuids);

    // Get artwork counts for each artist
    const artistsWithCounts = await Promise.all(
      artists.map(async (artist) => {
        const { count } = await supabase
          .from('vcs')
          .select('*', { count: 'exact', head: true })
          .eq('uuid', artist.uuid)
          .eq('access_control', 'Public');

        return {
          ...artist,
          artwork_count: count || 0
        };
      })
    );

    return {
      artists: artistsWithCounts,
      exportedAt: new Date().toISOString(),
      totalCount: artistsWithCounts.length
    };

  } catch (error) {
    console.error('Error exporting artist data:', error);
    throw error;
  }
};